import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Unexplained infertility with poor ovarian response — FSH receptor sensitivity + clotting
// and methylation context, framed as treatment planning rather than diagnosis.

export const infertilityElena: Scenario = {
  slug: 'infertility-elena',
  series: 'Hormones & Mood',
  title: 'The tests were reassuring. The IVF response was not.',
  socialHook:
    "Elena, 39, had normal AMH, open tubes, a good semen analysis, and two IVF cycles with fewer eggs than expected. Her genetics did not diagnose infertility. They gave her a better set of planning questions for the next cycle.",
  meta: {
    description:
      "A fictional case study: Elena, 39, has unexplained infertility and poor response to standard IVF stimulation despite reassuring baseline tests. FSHR, FSHB, MTHFR, and Factor V Leiden genetics do not diagnose infertility, but they reframe her next fertility-planning conversation.",
  },

  persona: {
    name: 'Elena',
    age: 39,
    sex: 'Female',
    ancestry: 'Italian American',
    occupation: 'Attorney',
    chiefConcern:
      "She and her partner have been trying to conceive for 22 months. Her tubes are open, her partner's semen analysis is normal, and her AMH is reassuring for her age. She has done two IVF cycles on a standard antagonist protocol. Both retrieved fewer eggs than expected, and both ended with no transferable embryo. The diagnosis on paper is 'unexplained infertility.' Elena does not feel unexplained. She feels like the protocol is missing something.",
    familyHistory:
      'Mother conceived easily. Maternal aunt had three miscarriages before a successful pregnancy. No known early menopause.',
  },

  clinicalPicture: {
    symptoms: [
      'Regular 29–31 day cycles',
      '22 months trying to conceive',
      'Two IVF cycles with unexpectedly low egg yield for AMH and age',
      'High emotional distress around being told the results are just bad luck',
    ],
    labs: [
      { name: 'AMH', value: '2.4 ng/mL', refRange: '1.0–4.0 ng/mL for age', flag: 'normal' },
      { name: 'Antral follicle count', value: '16', refRange: 'Expected 10–20', flag: 'normal' },
      { name: 'Peak estradiol during stimulation', value: 'Lower than expected', refRange: 'Protocol-dependent', flag: 'low' },
      { name: 'Homocysteine', value: '12.8 µmol/L', refRange: '<10 µmol/L optimal', flag: 'high' },
      { name: 'TSH', value: '2.6 mIU/L', refRange: '0.4–4.0 mIU/L', flag: 'normal' },
    ],
    medications: [
      'Prenatal vitamin with folic acid',
      'Two prior IVF antagonist cycles with standard FSH dosing',
    ],
    supplements: ['CoQ10 200 mg/day', 'Vitamin D3 2000 IU/day'],
    lifestyle: [
      'High-stress job, long workdays during both IVF cycles',
      'BMI 23.1',
      'Exercises 2–3 times/week',
      'No tobacco, alcohol stopped while trying to conceive',
      'Sleep: 6 hours on weekdays, worse during treatment cycles',
    ],
  },

  genetics: [
    {
      gene: 'FSHR',
      variant: 'Ser680Asn',
      rsid: 'rs6166',
      genotype: 'Ser/Ser — lower FSH receptor sensitivity pattern',
      plainMeaning:
      "Her egg reserve tests looked reassuring, but her ovaries may need a different medication signal. This receptor pattern can make standard FSH stimulation less effective for some patients.",
      technicalDetail:
        "FSHR encodes the follicle-stimulating hormone receptor on ovarian granulosa cells. The Ser680 pattern has been associated in multiple studies with altered ovarian sensitivity to exogenous FSH, often requiring higher cumulative doses or different stimulation strategies to achieve the expected follicle response. This does not diagnose infertility, but it can explain why AMH and follicle count looked reassuring while stimulation response was unexpectedly weak.",
    },
    {
      gene: 'FSHB',
      variant: 'Promoter variant',
      rsid: 'rs10835638',
      genotype: 'Lower endogenous FSH signaling tendency',
      plainMeaning:
      "The signal that recruits follicles may run quieter at baseline. That makes timing and protocol choice more important than AMH alone can show.",
      technicalDetail:
        "FSHB encodes the beta subunit of follicle-stimulating hormone. Promoter variation near FSHB is associated with lower circulating FSH levels and altered reproductive hormone dynamics. In fertility treatment, this may interact with FSHR sensitivity: the signal starts lower, and the receptor may respond less strongly. The clinical implication is not a single fixed dose, but a more individualized stimulation discussion.",
    },
    {
      gene: 'MTHFR',
      variant: 'C677T',
      rsid: 'rs1801133',
      genotype: 'T/T — reduced folate activation capacity',
      plainMeaning:
      "Her prenatal contains folic acid, but her body may need the active form more directly, especially with homocysteine above the ideal range.",
      technicalDetail:
        "MTHFR C677T homozygosity reduces conversion of folic acid into 5-MTHF, the active methylfolate form used in one-carbon metabolism. Elevated homocysteine can reflect that bottleneck and has been associated with poorer reproductive and vascular environments in some studies. This finding should be treated as modifiable context, not as the cause of infertility: switching to methylfolate with adequate B12, B6, and riboflavin support is a practical way to address the pathway.",
    },
    {
      gene: 'F5 (Factor V)',
      variant: 'Leiden (R506Q)',
      rsid: 'rs6025',
      genotype: 'Heterozygous — one copy',
      plainMeaning:
      "This does not explain the low egg yield. It matters for pregnancy planning, stimulation risk review, and miscarriage-risk conversations if she conceives.",
      technicalDetail:
        "Factor V Leiden increases clotting tendency by making Factor V resistant to activated Protein C. Its role in infertility is often overstated, but it is clinically relevant for pregnancy, miscarriage history, ovarian stimulation risk assessment, and thrombosis planning. In Elena's case, it belongs in the fertility specialist's risk review, especially given her family history of recurrent miscarriage.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'Every standard test said nothing obvious was wrong',
      body: "Elena's fertility workup was supposed to be reassuring. Her AMH was solid. Her antral follicle count looked good. Her tubes were open. Her partner's semen analysis was normal. The first IVF cycle was expected to retrieve a reasonable number of eggs. It did not. The second cycle adjusted a few details but ended the same way: fewer eggs than expected, weak embryo progression, no transfer. The phrase 'unexplained infertility' began to sound less like a diagnosis and more like a shrug.",
    },
    {
      type: 'narrative',
      heading: 'AMH measures the pool, not the response',
      body: "AMH and follicle count estimate how many recruitable follicles may be present. They do not tell you how strongly those follicles respond to the medication signal. IVF stimulation is a conversation between a drug and a receptor. If the receptor is less sensitive, a normal-looking pool can still respond weakly to a standard protocol. Elena's results were not contradictory. They were measuring different parts of the system.",
    },
    {
      type: 'reveal',
      heading: 'The standard dose may not have spoken loudly enough',
      body: "Elena's genetics do not say she cannot conceive. They point to a possible mismatch between her ovarian response and the standard stimulation assumptions. Her FSHR result suggests lower sensitivity to the hormone used to stimulate follicle growth, while FSHB may make the baseline signal quieter. That reframes the first two cycles: not proof that her ovaries are empty, but evidence that the protocol may not have matched her biology.",
    },
    {
      type: 'reveal',
      heading: 'The other clues were planning clues, not blame',
      body: "Two other findings matter before the next cycle, but neither should be treated as the cause of infertility. Her MTHFR result, paired with elevated homocysteine, suggests her prenatal folic acid may not be the best form for her methylation pathway. Her Factor V Leiden result does not explain egg retrieval, but it does matter for pregnancy planning and clot-risk review during stimulation. These are not magic answers. They are questions that help make the next protocol less generic.",
    },
    {
      type: 'action',
      heading: 'Five fertility conversations that become more specific',
      items: [
        'Ask the reproductive endocrinologist to review the prior cycles as possible low ovarian sensitivity, not simply poor ovarian reserve. AMH and follicle count were reassuring; response was the mismatch.',
        'Discuss whether the FSHR/FSHB context supports a different stimulation strategy: higher starting FSH, altered LH support, longer priming, or a protocol chosen for prior low response.',
        'Switch from folic acid to methylfolate with adequate B12, B6, and riboflavin, then recheck homocysteine. The goal is to normalize a measurable pathway before another high-stakes cycle.',
        'Bring Factor V Leiden into the stimulation and pregnancy-risk plan. This may affect clot precautions during IVF and early pregnancy, especially with family miscarriage history.',
        'Track sleep, stress load, supplements, and cycle symptoms in one place before the next protocol. The goal is not to blame stress; it is to give the specialist a cleaner baseline and reduce avoidable noise.',
      ],
    },
  ],

  extraBeats: [
    {
      type: 'streak' as const,
      heading: "Elena's pre-cycle preparation goal",
      goalLabel: 'Methylfolate protocol + sleep baseline before next IVF consult',
      caption:
        'Elena used the waiting period before her next specialist visit to address the modifiable methylation finding and document sleep during a calmer baseline phase.',
      weeks: [
        {
          weekLabel: 'Week 1',
          days: [true, true, false, true, true, false, true],
          note: 'Switched prenatal to methylfolate form. Work week chaotic, sleep still short.',
        },
        {
          weekLabel: 'Week 2',
          days: [true, true, true, true, false, true, true],
          note: 'B12 and riboflavin added. More consistent bedtime on weekdays.',
        },
        {
          weekLabel: 'Week 3',
          days: [true, true, true, true, true, true, false],
          note: 'Homocysteine recheck booked. IVF consult questions written down.',
        },
        {
          weekLabel: 'Week 4',
          days: [true, true, true, true, true, true, true],
          note: 'First fully consistent week. Feels less like guessing and more like preparing.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'infertility-elena',
    label: 'Explore your fertility and hormone-response genetics in Coach',
  },
}
